/** Chart pins: where a click or a drag lands, kept free of ECharts so the
 *  rule that matters is tested without a canvas. A pin sits on stored
 *  readings only (HANDBOOK, "Stored readings, and only those"): a click
 *  takes the nearest stored point's own time and value, never the cursor's
 *  time or a value interpolated between readings, and a gap is never a
 *  reading. */
import type { ChartPin, Series, SeriesPoint } from '../api/types'

/** How far, in screen pixels, a click may be from a line and still pin it. */
export const SNAP_PX = 24

export interface SnappedPoint { parameter: string; ts: string; value: number }
export interface Stretch { ts: string; ts_end: string; value: number; value_end: number }

/** The index of the point whose time is nearest `t`, or -1 for none. The
 *  points are in time order, as /api/series returns them. */
function nearestIndex(points: SeriesPoint[], t: number): number {
  let lo = 0
  let hi = points.length - 1
  if (hi < 0) return -1
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1
    if (Date.parse(points[mid]![0]) <= t) lo = mid
    else hi = mid
  }
  const dLo = Math.abs(Date.parse(points[lo]![0]) - t)
  const dHi = Math.abs(Date.parse(points[hi]![0]) - t)
  return dHi < dLo ? hi : lo
}

/** The stored reading a click at time `t` and height `cursorY` pins: on each
 *  series the reading nearest in time, and of those the one nearest the
 *  cursor on screen. None when the nearest reading of every series is a
 *  stored null (a gap), or when no line is within `maxPx`. Given `xPx`
 *  (time to screen pixels), a reading more than `maxPx` across from the
 *  cursor is refused too: an outage is an absence of points, and a click
 *  inside one would otherwise pin the reading at its edge, hours away. */
export function snapToStored(series: Series[], t: number,
                             yPx: (s: Series, v: number) => number,
                             cursorY: number, maxPx = SNAP_PX,
                             xPx?: (t: number) => number): SnappedPoint | null {
  let best: (SnappedPoint & { distance: number }) | null = null
  for (const s of series) {
    const i = nearestIndex(s.points, t)
    if (i < 0) continue
    const [ts, value] = s.points[i]!
    if (value === null) continue
    if (xPx && Math.abs(xPx(Date.parse(ts)) - xPx(t)) > maxPx) continue
    const distance = Math.abs(yPx(s, value) - cursorY)
    if (distance <= maxPx && (!best || distance < best.distance)) {
      best = { parameter: s.parameter, ts, value, distance }
    }
  }
  return best && { parameter: best.parameter, ts: best.ts, value: best.value }
}

/** The stretch a drag from `t0` to `t1` pins on one series: its first and
 *  last stored, non-null readings inside the drag, in either direction.
 *  None unless there are two. */
export function stretchOf(series: Series, t0: number, t1: number): Stretch | null {
  const lo = Math.min(t0, t1)
  const hi = Math.max(t0, t1)
  const inside = series.points.filter(([ts, value]) => {
    const t = Date.parse(ts)
    return value !== null && t >= lo && t <= hi
  })
  if (inside.length < 2) return null
  const first = inside[0]!
  const last = inside[inside.length - 1]!
  return { ts: first[0], ts_end: last[0], value: first[1]!, value_end: last[1]! }
}

/** The stored points of a pinned stretch, the very objects on the chart:
 *  the overlay that marks a stretch is a copy of readings already drawn,
 *  never a new one. Nothing for a pin on one value. */
export function pinOverlayPoints(series: Series, pin: ChartPin): SeriesPoint[] {
  if (pin.ts_end === null) return []
  const from = Date.parse(pin.ts)
  const to = Date.parse(pin.ts_end)
  return series.points.filter(([ts]) => {
    const t = Date.parse(ts)
    return t >= from && t <= to
  })
}
